import angular, {
    IAugmentedJQuery,
    IDocumentService,
    IOnChangesObject,
    IPromise,
    IQService,
    IRootScopeService,
    IScope,
    ITemplateCacheService,
    IWindowService,
} from 'angular';
import { CartService } from '../../tn.cart/service.tn.cart';
import {
    FontFamilyService,
    FontStyleService,
    IBucketOptions,
    ICartLicense,
    IFontFamilyModel,
    IFontStyleModel,
    ISubtotal,
} from '../../tn.common';
import { EMPTY_FONT_RIGHTS } from '../../tn.licenses/font-rights';
import { LicenseListService } from '../../tn.licenses/license-list.service/licenseList';
import {
    PopoverController,
    PopoverService,
} from '../../tn.popover/service.tn.popover';
import { BucketService } from '../../tn.pricing/buckets.service/buckets';
import { Pricing } from '../../tn.pricing/service.tn.pricing';
import { UserService } from '../../tn.user/service.tn.user';
import { StyleSelection } from '../style.selection.service/style-selection';
import BuyStylesAddStylesFromCartTemplate from './add-styles-from-cart.html';
import BuyStylesAddStylesNewTemplate from './add-styles-new.html';
import template from './buy-styles-popover.html';
import BuyStylesConfirmationFromCartTemplate from './confirmation-from-cart.html';
import BuyStylesConfirmationTemplate from './confirmation.html';
import BuyStylesCorrectLicensureTemplate from './correct-licensure.html';
import BuyStylesLicenseTypesTemplate from './license-types.html';
import BuyStylesPreviewOnlyTemplate from './preview-only.html';

export function populateBuyStylesTemplateCache(
    $templateCache: ITemplateCacheService
): void {
    $templateCache.put(
        'buy-styles.view/add-styles-from-cart.html',
        BuyStylesAddStylesFromCartTemplate
    );
    $templateCache.put(
        'buy-styles.view/add-styles-new.html',
        BuyStylesAddStylesNewTemplate
    );
    $templateCache.put(
        'buy-styles.view/license-types.html',
        BuyStylesLicenseTypesTemplate
    );
    $templateCache.put(
        'buy-styles.view/confirmation.html',
        BuyStylesConfirmationTemplate
    );
    $templateCache.put(
        'buy-styles.view/preview-only.html',
        BuyStylesPreviewOnlyTemplate
    );
    $templateCache.put(
        'buy-styles.view/confirmation-from-cart.html',
        BuyStylesConfirmationFromCartTemplate
    );
    $templateCache.put(
        'buy-styles.view/correct-licensure.html',
        BuyStylesCorrectLicensureTemplate
    );
}
populateBuyStylesTemplateCache.$inject = ['$templateCache'];

export class BuyStylesPopoverController extends PopoverController {
    // bindings
    // initial styles for this popover.
    styles: IFontStyleModel[] = [];
    class?: string; // will set classes on the popover element that is created.

    // locals
    protected $anchor: JQLite;
    // this is the styles selected with this popover,
    protected styleSelection = new StyleSelection();
    // this is the selected subset of styles to purchase.
    selectedStyles = this.styleSelection.get();
    protected cleanUpFontRightsWatcher?: () => void;
    protected cleanUpSelectedStylesWatcher?: () => void;

    states = {
        'add-styles-from-cart': {
            templateUrl: 'buy-styles.view/add-styles-from-cart.html',
        },
        'add-styles-new': {
            templateUrl: 'buy-styles.view/add-styles-new.html',
        },
        'license-types': {
            templateUrl: 'buy-styles.view/license-types.html',
        },
        confirmation: {
            templateUrl: 'buy-styles.view/confirmation.html',
        },
        'preview-only': {
            templateUrl: 'buy-styles.view/preview-only.html',
        },
        'confirmation-from-cart': {
            templateUrl: 'buy-styles.view/confirmation-from-cart.html',
        },
        'correct-licensure': {
            templateUrl: 'buy-styles.view/correct-licensure.html',
        },
    };
    currentState = 'add-styles-new';
    families = {};
    fontRights = angular.copy(EMPTY_FONT_RIGHTS);
    licenses = [];
    buckets = {};
    calculatedPrices = {
        web: {},
        web_self_hosted: {},
        app: {},
        cpu: {},
        ebook: {},
    };
    firstBucket?: IBucketOptions;
    cameFromCart = false;
    cameFromLicensure = false;
    cart = null;

    defaultStyles = {};

    isDesktopLicense = false;
    isWebLicense = false;
    isAppLicense = false;
    isEpubLicense = false;
    loadingStyles?: IPromise<IFontStyleModel[]>;
    showError = false;
    webState = 'hosted';

    selectLabels: {
        desktop?: string;
        epub?: string;
        appApps?: string;
        appYears?: string;
        webViews?: string;
        webYears?: string;
    } = {};
    subTotal: ISubtotal = {
        base: 0,
        discounted: 0,
        adjustedTotal: 0,
        priceAdjustment: 0,
    };

    // #region state: add-styles-from-cart props
    cartFamily?;
    displayLoading = false;
    familyQuery = '';
    showAddStylesCartError = false;
    // #endregion

    static $inject = [
        'tn.cart.service',
        'tnBuckets',
        'tn.pricing.service',
        'tnLicenseListService',
        'tn.user.service',
        '$q',
        '$rootScope',
        '$tnPopover',
        '$window',
        '$document',
        '$element',
        '$scope',
        'FontFamilyPublicService',
        'FontStylePublicService',
    ];
    constructor(
        protected cartService: CartService,
        protected bucketsService: BucketService,
        protected pricingService: Pricing,
        protected licenseListService: LicenseListService,
        protected userService: UserService,
        protected $q: IQService,
        protected $rootScope: IRootScopeService,
        protected $tnPopover: PopoverService,
        $window: IWindowService,
        $document: IDocumentService,
        $element: IAugmentedJQuery,
        protected $scope: IScope,
        protected familyService: FontFamilyService,
        protected styleService: FontStyleService
    ) {
        super($window, $document, $element.children().first());
        this.$anchor = $element.parent();
        this.class = $element.attr('class');
        this.$tnPopover.popovers.push(this);
        this.styleSelection.selection$.subscribe((styles) => {
            this.selectedStyles = styles;
            if (styles.length === 0) {
                this.resetSelections();
                this.$tnPopover.hide();
            }
            this._updateFamilies()
                .then(() => this._updateBuckets())
                .then(() => this.calculateSubtotal());
        });
    }

    $onChanges(onChangesObj: IOnChangesObject): void {
        // Update the subtotal as the fontRights change
        const styles = onChangesObj.styles;
        const stylesChanged =
            styles && styles.currentValue !== styles.previousValue;
        if (stylesChanged) {
            // re-initialize styleSelection when binding is updated.
            if (this.styles) this.styleSelection.selectAll(this.styles);
            else this.styleSelection.selectNone();
        }
    }

    $onDestroy(): void {
        this.cleanUpFontRightsWatcher && this.cleanUpFontRightsWatcher();
        this.cleanUpSelectedStylesWatcher &&
            this.cleanUpSelectedStylesWatcher();
    }

    //Called when popover opens, sets up license template and looks up family buckets
    show(): void {
        this.cart = this.cartService.getCart();
        this.cameFromCart = false;
        this.familyQuery = '';
        this.cartFamily = null;
        this.showAddStylesCartError = false;
        this.cleanUpFontRightsWatcher = this.$scope.$watch(
            'popover.fontRights',
            () => {
                this._updateBuckets().then(() => this.calculateSubtotal());
            },
            true
        );
        super.show(this.$anchor[0]);
    }

    // override base popo
    clickedOffPopover(event: Event): void {
        const target = angular.element(event.target as HTMLElement);
        const parentIsTypeaheadChoice =
            target.parents('li[id*="typeahead"]').length > 0;
        const isInside =
            this.element[0] === event.target ||
            this.$anchor[0] === event.target ||
            this.element.has(event.target as HTMLElement).length > 0 ||
            this.$anchor.has(event.target as HTMLElement).length > 0 ||
            parentIsTypeaheadChoice || //Workaround for #434
            target.closest('.prevent_close').length > 0;
        if (!isInside) {
            this.hide();
        }
    }

    toggleStyles(styles: IFontStyleModel[]): void {
        this.styleSelection.toggle(styles);
    }

    resetSelections(): void {
        // XXX: reset code
        this.fontRights = angular.copy(EMPTY_FONT_RIGHTS);
        this.isDesktopLicense = false;
        this.isWebLicense = false;
        this.isAppLicense = false;
        this.isEpubLicense = false;
    }

    removeFamily(family: IFontFamilyModel): void {
        this.removeStyles(
            this.styleSelection.selection$
                .getValue()
                .filter((s) => family.style.indexOf(s.id) > -1)
        );
    }

    removeStyle(style: IFontStyleModel): void {
        return this.removeStyles([style]);
    }

    removeStyles(styles: IFontStyleModel[]): void {
        this.styleSelection.toggle(styles);
    }

    _updateFamilies(): angular.IPromise<void> {
        //Only fetch families that we don't have yet
        const familiesSet = this.selectedStyles.reduce<Set<number>>(
            (acc, style) => {
                acc.add(style.family);
                return acc;
            },
            new Set()
        );
        const familyIds = Array.from(familiesSet);
        if (familyIds.length === 0) return this.$q.resolve();
        return this.familyService
            .list({ id: familyIds, page_size: 10000 })
            .then(({ results: families }) => {
                this.families = families.reduce((acc, family) => {
                    acc[family.id] = family;
                    return acc;
                }, {});
            });
    }

    _updateBuckets(): Promise<unknown[]> {
        const promise = this.bucketsService.getIntersectingBuckets(
            this.selectedStyles
        );

        // ensure that all promises have been complete before continuation
        return promise.then((intersectingBuckets) => {
            const promises: unknown[] = [];
            // set first bucket to use in templates
            const firstBucket = intersectingBuckets;
            this.bucketsService.getClosestAcceptableFontRights(
                intersectingBuckets,
                this.fontRights,
                true
            );
            const calculatedPrices = this.calculatedPrices;
            if (firstBucket && Object.keys(firstBucket).length !== 0) {
                // get all the different kinds of licenses in there ...
                ['app', 'cpu', 'ebook', 'web_self_hosted', 'web'].forEach(
                    (key) =>
                        firstBucket[key].forEach((bucket) => {
                            // TODO: simplify this.
                            switch (key) {
                                case 'web_self_hosted':
                                case 'web':
                                    firstBucket['year'].forEach((term) => {
                                        const promise = this.calculateWebPrice(
                                            bucket.key,
                                            term.key
                                        ).then((total) => {
                                            calculatedPrices[key][term.key] ??=
                                                {};
                                            calculatedPrices[key][term.key][
                                                bucket.key
                                            ] = total;
                                        });
                                        promises.push(promise);
                                    });
                                    break;
                                case 'cpu': {
                                    const promise = this.calculateCpuPrice(
                                        bucket.key
                                    ).then(
                                        (total) =>
                                            (calculatedPrices[key][bucket.key] =
                                                total)
                                    );
                                    promises.push(promise);
                                    break;
                                }
                                case 'ebook': {
                                    const promise = this.calculateEpubPrice(
                                        bucket.key
                                    ).then(
                                        (total) =>
                                            (calculatedPrices[key][bucket.key] =
                                                total)
                                    );
                                    promises.push(promise);
                                    break;
                                }
                                case 'app':
                                    firstBucket['year'].forEach((term) => {
                                        const promise = this.calculateAppPrice(
                                            bucket.key,
                                            term.key
                                        ).then((total) => {
                                            if (
                                                calculatedPrices[key][
                                                    term.key
                                                ] === undefined
                                            ) {
                                                calculatedPrices[key][
                                                    term.key
                                                ] = {};
                                            }
                                            calculatedPrices[key][term.key][
                                                bucket.key
                                            ] = total;
                                        });
                                        promises.push(promise);
                                    });
                                    break;
                            }
                        })
                );
            }
            // console.log('calcalatedPrices', calculatedPrices);
            this.firstBucket = firstBucket;
            this._reapplyLabels();
            return this.$q.all(promises);
        });
    }

    goToState(state): void {
        if (state === 'add-styles-from-cart') {
            this.cameFromCart = true;
        }

        if (state === 'correct-licensure') {
            this.cameFromLicensure = true;
        }
        if (Object.keys(this.states).indexOf(state) > -1) {
            this.currentState = state;
        } else {
            //console.log('state', state, 'does not exist');
        }
    }

    goBackFromLicenses(): void {
        if (
            this.currentState === 'license-types' ||
            this.currentState === 'add-styles-new'
        ) {
            if (this.cameFromCart) {
                this.goToState('add-styles-from-cart');
            } else if (this.cameFromLicensure) {
                this.goToState('correct-licensure');
            }
        }
    }

    addToCart(): void {
        if (
            !(
                this.isDesktopLicense ||
                this.isWebLicense ||
                this.isAppLicense ||
                this.isEpubLicense
            )
        ) {
            this._showError();
            return;
        }

        this._hideError();

        // `this.cameFromCart` here is the `allowRemoval` param; edit on cart does allowRemoval, but not in catalogue
        this.licenseListService.addLicenses(
            this.cart.license,
            this._buildLicense(),
            this.licenseListService.getLicenseTypeArray(
                this.isAppLicense,
                this.isDesktopLicense,
                this.isEpubLicense,
                this.isWebLicense
            ),
            this.cameFromCart
        );

        if (this.cart.id !== undefined && this.cart.id !== null) {
            this.cartService.updateCartOnServer(this.cart);
        }

        if (this.cameFromCart) {
            this.goToState('confirmation-from-cart');
        } else if (this.cameFromLicensure) {
            this.$rootScope.$broadcast('tn.fonts.buyStyles::continueCart');
        } else {
            this.goToState('confirmation');
        }
    }

    calculateAppPrice(apps, years): angular.IPromise<number> {
        const licenses = this.selectedStyles.map<ICartLicense>((style) => ({
            fontRights: { app: { apps: apps, years: years } },
            font: style,
        }));
        return this.pricingService
            .calculateLicensePrices(licenses)
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    calculateCpuPrice(computers): angular.IPromise<number> {
        const licenses = this.selectedStyles.map<ICartLicense>((style) => ({
            fontRights: { desktop: { computers: computers } },
            font: style,
        }));
        return this.pricingService
            .calculateLicensePrices(licenses)
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    calculateEpubPrice(ebooks): angular.IPromise<number> {
        const licenses = this.selectedStyles.map<ICartLicense>((style) => ({
            fontRights: { epub: { ebooks: ebooks } },
            font: style,
        }));
        return this.pricingService
            .calculateLicensePrices(licenses)
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    calculateWebPrice(views, years): angular.IPromise<number> {
        const selfHosted = this.webState === 'self-hosted';
        const licenses = this.selectedStyles.map<ICartLicense>((style) => ({
            fontRights: {
                web: {
                    views: views,
                    years: years,
                    self_hosted: selfHosted,
                },
            },
            font: style,
        }));

        return this.pricingService
            .calculateLicensePrices(licenses)
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    calculateSubtotal(): angular.IPromise<ISubtotal> {
        const licenses = this._buildLicense();
        return this.pricingService
            .calculateSubTotal(licenses)
            .then((subTotal) => (this.subTotal = subTotal));
    }

    setWebState(state): void {
        this.webState = state;
        this.toggleSelect('web', true);
    }

    /**
     * _reapplyLabels depends on this.firstBucket being set. do not call
     * until after it is assigned
     */
    _reapplyLabels(): void {
        if (this.isDesktopLicense) {
            this.selectLabels.desktop = this.firstBucket?.cpu.filter(
                (b) => b.key === this.fontRights.desktop?.computers
            )[0].label;
        }
        if (this.isEpubLicense) {
            this.selectLabels.epub = this.firstBucket?.ebook.filter(
                (b) => b.key === this.fontRights.epub?.ebooks
            )[0].label;
        }
        if (this.isAppLicense) {
            this.selectLabels.appApps = this.firstBucket?.app.filter(
                (b) => b.key === this.fontRights.app?.apps
            )[0].label;
            this.selectLabels.appYears = this.firstBucket?.year.filter(
                (b) => b.key === this.fontRights.app?.years
            )[0].label;
        }
        if (this.isWebLicense) {
            switch (this.webState) {
                case 'hosted':
                    this.selectLabels.webViews = this.firstBucket?.web.filter(
                        (b) => b.key === this.fontRights.web?.views
                    )[0].label;
                    this.selectLabels.webYears = this.firstBucket?.year.filter(
                        (b) => b.key === this.fontRights.web?.years
                    )[0].label;
                    break;
                case 'self-hosted':
                    this.selectLabels.webViews =
                        this.firstBucket?.web_self_hosted.filter(
                            (b) => b.key === this.fontRights.web?.views
                        )[0].label;
                    this.selectLabels.webYears = this.firstBucket?.year.filter(
                        (b) => b.key === this.fontRights.web?.years
                    )[0].label;
                    break;
            }
        }
    }

    toggleSelect(selectName, isActive): void {
        let webLabel;
        if (isActive) {
            this._hideError();
            const bucketTemplate = this.firstBucket;
            switch (selectName) {
                case 'app':
                    this.fontRights.app = {
                        apps: bucketTemplate?.app[0].key,
                        years: bucketTemplate?.year[0].key,
                    };
                    this.selectLabels.appApps =
                        bucketTemplate?.app[0].key.toString();
                    this.selectLabels.appYears =
                        bucketTemplate?.year[0].label; /*+
                    (bucketTemplate.year[0].key === 1 ? ' year' : ' years');*/
                    break;
                case 'desktop':
                    this.fontRights.desktop = {
                        computers: bucketTemplate?.cpu[0].key,
                    };
                    this.selectLabels.desktop = bucketTemplate?.cpu[0].label;
                    break;
                case 'epub':
                    this.fontRights.epub = {
                        ebooks: bucketTemplate?.ebook[0].key,
                    };
                    this.selectLabels.epub = bucketTemplate?.ebook[0].label;
                    break;
                case 'web':
                    webLabel = '';
                    switch (this.webState) {
                        case 'hosted':
                            webLabel = bucketTemplate?.web[0].label;
                            this.fontRights.web = {
                                years: bucketTemplate?.year[0].key,
                                views: bucketTemplate?.web[0].key,
                                self_hosted: false,
                            };
                            break;
                        case 'self-hosted':
                            webLabel = bucketTemplate?.web_self_hosted[0].label;
                            this.fontRights.web = {
                                years: bucketTemplate?.year[0].key,
                                views: bucketTemplate?.web_self_hosted[0].key,
                                self_hosted: true,
                            };
                            break;
                    }
                    this.selectLabels.webViews = webLabel;
                    this.selectLabels.webYears = bucketTemplate?.year[0].label;
            }
        } else {
            switch (selectName) {
                case 'app':
                    if (this.fontRights.app) {
                        this.fontRights.app.apps = null;
                        this.fontRights.app.years = null;
                    }
                    break;
                case 'desktop':
                    if (this.fontRights.desktop)
                        this.fontRights.desktop.computers = null;
                    break;
                case 'epub':
                    if (this.fontRights.epub)
                        this.fontRights.epub.ebooks = null;
                    break;
                case 'web':
                    if (this.fontRights.web) {
                        this.fontRights.web.years = null;
                        this.fontRights.web.visits = null;
                        this.fontRights.web.views = null;
                    }
            }
        }
    }

    removeFromCart(styleOrStyles): void {
        if (!Array.isArray(styleOrStyles)) {
            styleOrStyles = [styleOrStyles];
        }

        styleOrStyles.forEach((style) => {
            this.licenseListService.removeLicense(
                this.cart.license,
                this.cart.license.filter((l) => l.font.id === style.id)
            );
        });
        this.toggleStyles(styleOrStyles);

        if (this.selectedStyles.length === 0) {
            this.$rootScope.$broadcast('tn.fonts.buyStyles::continueCart');
        }
    }

    removeAllFromCart(): void {
        return this.removeFromCart(this.selectedStyles);
    }

    _showError(): void {
        this.showError = true;
    }

    _hideError(): void {
        this.showError = false;
    }

    _buildLicense(): never[] {
        this.licenses = [];

        this.selectedStyles.forEach((style) => {
            const license = {
                font: style,
                fontRights: angular.copy(this.fontRights),
            };
            this.licenses.push(license);
        });

        return this.licenses;
    }

    // #region state: add-styles-from-cart state
    continueAddStylesFromCart(): void {
        if (this.loadingStyles) {
            this.displayLoading = true;
        }
        const stylePromise = this.loadingStyles || this.$q.resolve([]);

        stylePromise.then(() => {
            if (
                this.cartFamily &&
                this.cartFamily.style &&
                this.cartFamily.style.length
            ) {
                this.goToState('add-styles-new');
            } else {
                this.showAddStylesCartError = true;
            }
            this.displayLoading = false;
        });
    }

    // this is called when a family is selected in the add-styles-from-cart state
    selectFamily(item: IFontFamilyModel): void {
        this.families[item.id] = item;
        this.cartFamily = item;
        if (item.style.length === 0) {
            this.loadingStyles = this.$q.resolve([]);
        } else {
            this.loadingStyles = this.styleService
                .list({ id: item.style, page_size: 10000 })
                .then(({ results: styles }) => {
                    this.styles = styles;
                    this.styleSelection.selectAll(styles);
                    this._updateBuckets().then(() => this.calculateSubtotal());
                    this.loadingStyles = undefined;
                    return styles;
                });
        }
    }

    // this is called for the family autocomplete in the add-styles-from-cart state
    searchFontFamilies(familyName: string): IPromise<IFontFamilyModel[]> {
        this.showAddStylesCartError = false;
        return this.familyService
            .list({ search: familyName })
            .then(({ results: matchedFamilies }) => {
                const user = this.userService.getUser();

                const families = matchedFamilies.filter(
                    (fam) =>
                        // 3 == everyone
                        fam.visible === 3 ||
                        // 1 == staff only
                        fam.visible === 1 ||
                        // or if the user is a member of the foundry
                        fam.foundry.some(
                            (foundry) =>
                                user.foundry &&
                                user.foundry.length !== 0 &&
                                user.foundry.indexOf(foundry) !== -1
                        )
                );

                //limit to 6 matches
                return families.splice(0, 6);
            });
    }
    // #endregion
}

export const BuyStylesPopoverComponentOptions: angular.IComponentOptions = {
    controller: BuyStylesPopoverController,
    controllerAs: 'popover',
    template: template,
    bindings: {
        styles: '<',
    },
};
